import { useEffect } from "react";
import type { FC } from "react";
import { Button } from "react-bootstrap";

import { useAppSelector, useAppDispatch } from "store/hooks";
import { getTopicAssignments, setTopicAssignments } from "store/slices/assignmentNavigation";

import { useLazyGetHistorySessionsQuery } from "api/generated";

import { processApiError } from "utils/processApiError";

import IconArrowLeft from "images/icons/arrow-left.svg";
import IconArrowRight from "images/icons/arrow-right.svg";

interface Props {
  userAssignmentId: string;
  sessionId: string;
  onNavigate: (assignmentId: string) => void;
}

export const SessionHistoryAssignmentsNavigation: FC<Props> = ({ userAssignmentId, sessionId, onNavigate }) => {
  const assignmentNavigation = useAppSelector(getTopicAssignments(sessionId));
  const dispatch = useAppDispatch();

  const [getAssignmentsData, { error: apiLazyError }] = useLazyGetHistorySessionsQuery();

  useEffect(() => {
    if (apiLazyError === undefined) return;

    processApiError(apiLazyError);
    dispatch(setTopicAssignments({ topicId: sessionId, assignments: [] }));
  }, [apiLazyError]);

  useEffect(() => {
    if (assignmentNavigation === undefined) {
      getAssignmentsData()
        .then(response => {
          const session = response.data?.userTutoringsAndSessions.find(item => item.session?.id === sessionId);

          if (!session) return;

          const assignments = session.session?.userAssignments.map(assignment => assignment.id) ?? [];
          dispatch(setTopicAssignments({ topicId: sessionId, assignments }));
        })
        .catch(error => {
          console.error(error);
          dispatch(setTopicAssignments({ topicId: sessionId, assignments: [] }));
        });
    }
  }, [assignmentNavigation]);

  const navigatePrevious = () => {
    if (assignmentNavigation === undefined || assignmentNavigation.length <= 1) return;

    const index = assignmentNavigation.indexOf(userAssignmentId);

    if (index === -1) return;
    if (index === 0) {
      onNavigate(assignmentNavigation[assignmentNavigation.length - 1]);
      return;
    }

    onNavigate(assignmentNavigation[index - 1]);
  };

  const navigateNext = () => {
    if (assignmentNavigation === undefined || assignmentNavigation.length <= 1) return;

    const index = assignmentNavigation.indexOf(userAssignmentId);

    if (index === -1) return;
    if (index >= assignmentNavigation.length - 1) {
      onNavigate(assignmentNavigation[0]);
      return;
    }

    onNavigate(assignmentNavigation[index + 1]);
  };

  return (
    <div className="assignment-navigation">
      <Button variant={"select"} className={"ms-4px btn-shadow"} onClick={navigatePrevious}>
        <IconArrowLeft />
      </Button>
      <Button variant={"select"} className={"ms-4px btn-shadow"} onClick={navigateNext}>
        <IconArrowRight />
      </Button>
    </div>
  );
};
